import { Button, Checkbox, Col, Empty, Form, Image, Input, InputNumber, Modal, Row, Select, Space, Spin, Tag, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";

import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useRequest from "../../../hooks/useRequest";

const { TextArea } = Input;

const parseUrls = (value = "") =>
  Array.from(
    new Set(
      String(value || "")
        .split(/\r?\n/g)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );

const normalizeOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      value: item?._id || item?.id || item?.value || item,
      label: item?.label || item?.name || item?.title || item?.slug || item?.value || item?._id || item,
      raw: item?.raw || item,
    }))
    .filter((item) => item.value);

const normalizeIdList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => item?._id || item?.id || item?.value || item).filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const getProductKey = (product = {}) => String(product?.sourceUrl || product?.productUrl || product?.title || "").trim();

const cloneMeta = (meta = {}) => ({
  category_ids: normalizeIdList(meta?.category_ids),
  sub_category_ids: normalizeIdList(meta?.sub_category_ids),
  occasion_ids: normalizeIdList(meta?.occasion_ids),
  keywords_ids: normalizeIdList(meta?.keywords_ids),
});

const emptyMeta = () => cloneMeta({});

const getSubCategoryCategoryIds = (subCategory = {}) => {
  const source = subCategory?.category_ids || subCategory?.category || [];
  const normalized = Array.isArray(source) ? source : [source];
  return normalized.map((item) => item?._id || item?.id || item?.value || item).filter(Boolean).map(String);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "-";
  }

  return `Rs. ${parsed.toLocaleString("en-IN")}`;
};

const ScrapeProductModal = ({ api, show, hide, refresh }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [stage, setStage] = useState("form");
  const [previewProducts, setPreviewProducts] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [activeProductKey, setActiveProductKey] = useState("");
  const [metadataByKey, setMetadataByKey] = useState({});
  const [pageType, setPageType] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [occasionOptions, setOccasionOptions] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const scraperBase = api?.scraper || "scraper";

  const activeProduct = useMemo(() => previewProducts.find((item) => getProductKey(item) === activeProductKey) || previewProducts[0] || null, [activeProductKey, previewProducts]);
  const activeKey = getProductKey(activeProduct);
  const activeMeta = metadataByKey[activeKey] || emptyMeta();
  const selectedProducts = useMemo(() => previewProducts.filter((item) => selectedKeys.includes(getProductKey(item))), [previewProducts, selectedKeys]);
  const allSelected = previewProducts.length > 0 && selectedKeys.length === previewProducts.length;
  const activeCategoryIds = useMemo(() => activeMeta.category_ids || [], [activeMeta.category_ids]);
  const filteredSubCategoryOptions = useMemo(() => {
    const normalized = normalizeOptions(subCategoryOptions);
    if (!activeCategoryIds.length) {
      return normalized;
    }

    return normalized.filter((item) => getSubCategoryCategoryIds(item.raw).some((categoryId) => activeCategoryIds.includes(categoryId)));
  }, [activeCategoryIds, subCategoryOptions]);

  const loadLookup = (endpoint, setter) =>
    new Promise((resolve) => {
      request({
        url: endpoint,
        method: "GET",
        onSuccess: (response) => {
          setter(normalizeOptions(response?.data?.docs || response?.data || []));
          resolve(true);
        },
        onError: () => resolve(false),
      });
    });

  const loadAllLookups = async () => {
    await Promise.all([
      loadLookup(api.categories, setCategoryOptions),
      loadLookup(api.subCategories, setSubCategoryOptions),
      loadLookup(api.occasions, setOccasionOptions),
      loadLookup(api.keywords, setKeywordOptions),
    ]);
  };

  useEffect(() => {
    if (show) {
      loadAllLookups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (stage !== "preview" || !previewProducts.length) {
      return;
    }

    setMetadataByKey((prev) => {
      const merged = { ...prev };
      let changed = false;

      previewProducts.forEach((product) => {
        const key = getProductKey(product);
        if (!merged[key]) {
          merged[key] = emptyMeta();
          changed = true;
        } else {
          merged[key] = cloneMeta(merged[key]);
        }
      });

      return changed ? merged : prev;
    });

    if (!activeProductKey) {
      setActiveProductKey(getProductKey(previewProducts[0]));
    }
  }, [activeProductKey, previewProducts, stage]);

  const resetState = () => {
    setPreviewLoading(false);
    setSaveLoading(false);
    setStage("form");
    setPreviewProducts([]);
    setSelectedKeys([]);
    setActiveProductKey("");
    setMetadataByKey({});
    setPageType("");
    setSourceUrl("");
    form.resetFields();
  };

  const toggleProduct = (productKey) => {
    setActiveProductKey(productKey);
    setSelectedKeys((current) => {
      if (current.includes(productKey)) {
        return current.filter((item) => item !== productKey);
      }

      return [...current, productKey];
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedKeys([]);
      return;
    }

    setSelectedKeys(previewProducts.map((item) => getProductKey(item)).filter(Boolean));
  };

  const updateActiveMeta = (field, value) => {
    if (!activeKey) {
      return;
    }

    setMetadataByKey((prev) => {
      const next = { ...prev };
      const current = cloneMeta(next[activeKey] || emptyMeta());

      if (field === "category_ids") {
        const nextCategories = Array.isArray(value) ? value : [];
        current.category_ids = nextCategories;

        const allowedSubCategoryIds = normalizeOptions(subCategoryOptions)
          .filter((item) => getSubCategoryCategoryIds(item.raw).some((categoryId) => nextCategories.includes(categoryId)))
          .map((item) => item.value);

        current.sub_category_ids = current.sub_category_ids.filter((id) => allowedSubCategoryIds.includes(id));
      } else if (field === "sub_category_ids") {
        current.sub_category_ids = Array.isArray(value) ? value : [];
      } else if (field === "occasion_ids") {
        current.occasion_ids = Array.isArray(value) ? value : [];
      } else if (field === "keywords_ids") {
        current.keywords_ids = Array.isArray(value) ? value : [];
      }

      next[activeKey] = current;
      return next;
    });
  };

  const handlePreview = (values) => {
    const urls = parseUrls(values.urls);
    const limit = Number(values.limit);

    if (!urls.length) {
      ShowToast(lang("Please add at least one product URL."), Severty.ERROR);
      return;
    }

    if (!Number.isFinite(limit) || limit < 1) {
      ShowToast(lang("Please enter a valid limit."), Severty.ERROR);
      return;
    }

    setPreviewLoading(true);
    request({
      url: `${scraperBase}/preview`,
      method: "POST",
      data: {
        urls,
        url: urls[0],
        limit,
      },
      onSuccess: (response) => {
        setPreviewLoading(false);
        if (response.status) {
          const products = Array.isArray(response?.data?.products) ? response.data.products : [];
          if (!products.length) {
            ShowToast(response.message || lang("No visible products found."), Severty.ERROR);
            return;
          }

          const nextActiveKey = getProductKey(products[0]);
          const nextMeta = {};
          products.forEach((product) => {
            const key = getProductKey(product);
            nextMeta[key] = emptyMeta();
          });

          setPreviewProducts(products);
          setSelectedKeys([]);
          setActiveProductKey(nextActiveKey);
          setMetadataByKey(nextMeta);
          setPageType(response?.data?.pageType || "");
          setSourceUrl(response?.data?.sourceUrl || urls[0]);
          setStage("preview");
          ShowToast(response.message || lang("Preview loaded successfully."), Severty.SUCCESS);
        } else {
          ShowToast(response.message || lang("Scraping completed with issues."), Severty.ERROR);
        }
      },
      onError: (error) => {
        setPreviewLoading(false);
        ShowToast(error?.response?.data?.message || error?.message || lang("Scraping failed."), Severty.ERROR);
      },
    });
  };

  const handleSaveSelected = () => {
    if (!selectedProducts.length) {
      ShowToast(lang("Please select at least one product."), Severty.ERROR);
      return;
    }

    setSaveLoading(true);
    request({
      url: `${scraperBase}/save-selected`,
      method: "POST",
      data: {
        products: selectedProducts.map((product) => {
          const key = getProductKey(product);
          const meta = cloneMeta(metadataByKey[key] || emptyMeta());

          return {
            ...product,
            ...meta,
          };
        }),
      },
      onSuccess: (response) => {
        setSaveLoading(false);
        if (response.status) {
          const summary = response?.data?.summary || {};
          ShowToast(response.message || `Saved ${summary.created || 0} products.`, Severty.SUCCESS);
          resetState();
          hide();
          refresh();
        } else {
          ShowToast(response.message || lang("Saving completed with issues."), Severty.ERROR);
        }
      },
      onError: (error) => {
        setSaveLoading(false);
        ShowToast(error?.response?.data?.message || error?.message || lang("Saving failed."), Severty.ERROR);
      },
    });
  };

  return (
    <Modal
      open={show}
      width={1280}
      okText={stage === "form" ? lang("Start Scraping") : lang("Save Selected")}
      cancelText={lang("Cancel")}
      onCancel={() => {
        if (!previewLoading && !saveLoading) {
          resetState();
          hide();
        }
      }}
      okButtonProps={{
        form: "scrape-product-form",
        htmlType: "submit",
        loading: previewLoading || saveLoading,
        disabled: previewLoading || saveLoading || (stage === "preview" && !selectedProducts.length),
      }}
      cancelButtonProps={{
        disabled: previewLoading || saveLoading,
      }}
      centered
      className="tab_modal"
      maskClosable={!previewLoading && !saveLoading}
    >
      <Spin spinning={previewLoading || saveLoading}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h4 className="modal_title_cls">{lang("Scrape & Add Product")}</h4>
            <p className="text-muted mb-0">
              {stage === "preview"
                ? lang("Select products, assign categories and save only the checked items.")
                : lang("Paste product or listing URL(s), preview products first, then choose which ones to save.")}
            </p>
          </div>

          {stage === "preview" ? (
            <Space wrap>
              {pageType ? <Tag color="red">{String(pageType).toUpperCase()}</Tag> : null}
              <Tag color="blue">
                {selectedProducts.length} {lang("Selected")}
              </Tag>
              <Tag>
                {previewProducts.length} {lang("Found")}
              </Tag>
            </Space>
          ) : null}
        </div>

        <Form
          id="scrape-product-form"
          form={form}
          onFinish={stage === "form" ? handlePreview : handleSaveSelected}
          layout="vertical"
          initialValues={{
            urls: "",
            limit: 20,
          }}
        >
          {stage === "form" ? (
            <>
              <Form.Item
                label={lang("Product URLs")}
                name="urls"
                rules={[{ required: true, message: lang("Please paste one or more product URLs!") }]}
                extra={lang("Paste one URL per line. The first valid URL will be previewed.")}
              >
                <TextArea rows={8} placeholder={lang("https://www.amazon.in/s?k=study+ball+pen\nhttps://www.meesho.com/search?q=study%20item")} />
              </Form.Item>

              <Form.Item
                label={lang("Limit")}
                name="limit"
                rules={[{ required: true, message: lang("Please enter a limit!") }]}
              >
                <InputNumber min={1} max={100} style={{ width: "100%" }} placeholder={lang("Enter limit")} />
              </Form.Item>
            </>
          ) : (
            <Row gutter={[16, 16]}>
              <Col span={24} xl={16}>
                <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="text-muted text-break">
                    {lang("Source")} : <span className="fw-semibold text-dark">{sourceUrl || "-"}</span>
                  </div>

                  <Button type="link" onClick={toggleAll} disabled={previewLoading || saveLoading}>
                    {allSelected ? lang("Unselect All") : lang("Select All")}
                  </Button>
                </div>

                {previewProducts.length ? (
                  <div className="preview-product-grid">
                    {previewProducts.map((product) => {
                      const productKey = getProductKey(product);
                      const checked = selectedKeys.includes(productKey);
                      const active = activeProductKey === productKey;

                      return (
                        <div
                          key={productKey}
                          className={`preview-product-card ${checked ? "selected" : ""} ${active ? "active" : ""}`}
                          onClick={() => toggleProduct(productKey)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleProduct(productKey);
                            }
                          }}
                        >
                          <div className="preview-product-image">
                            <Checkbox className="preview-product-checkbox" checked={checked} />
                            <Image className="preview-product-thumb" src={product?.image || "/favicon.svg"} alt={product?.title || "product"} preview={false} />
                          </div>

                          <div className="preview-product-body">
                            <Typography.Title level={5} ellipsis={{ rows: 2, tooltip: product?.title }}>
                              {product?.title || "-"}
                            </Typography.Title>
                            <div className="d-flex justify-content-between align-items-center gap-2">
                              <span className="fw-semibold">{formatCurrency(product?.price)}</span>
                              <span className="text-muted small">{product?.rating ? `${Number(product.rating).toFixed(1)} rating` : "No rating"}</span>
                            </div>
                            <p className="text-muted small mb-0 text-truncate">{product?.shortDescription || lang("Visible product")}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Empty description={lang("No preview data found.")} />
                )}
              </Col>

              <Col span={24} xl={8}>
                <div className="preview-meta-panel">
                  {activeProduct ? (
                    <>
                      <div className="preview-meta-header">
                        <Image className="preview-meta-thumb" src={activeProduct?.image || "/favicon.svg"} alt={activeProduct?.title || "product"} preview={false} />
                        <div className="preview-meta-copy">
                          <Tag color={selectedKeys.includes(activeKey) ? "green" : "red"}>{selectedKeys.includes(activeKey) ? lang("Selected") : lang("Not selected")}</Tag>
                          <Typography.Title level={4} ellipsis={{ rows: 2, tooltip: activeProduct?.title }} className="mb-1">
                            {activeProduct?.title || "-"}
                          </Typography.Title>
                          <p className="text-muted mb-1">{formatCurrency(activeProduct?.price)}</p>
                          <p className="text-muted small mb-0">{activeProduct?.shortDescription || lang("Assign catalog data for this item before saving.")}</p>
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="mb-3 d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-bold">{lang("Manual Mapping")}</div>
                            <div className="text-muted small">{lang("These values will be saved with the active selected product.")}</div>
                          </div>
                        </div>

                        <Form.Item label={lang("Categories")} className="mb-3">
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={activeMeta.category_ids}
                            placeholder={lang("Select Categories")}
                            optionFilterProp="label"
                            onChange={(values) => updateActiveMeta("category_ids", values)}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                          >
                            {categoryOptions.map((item) => (
                              <Select.Option key={item.value} value={item.value} label={item.label}>
                                {item.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item label={lang("Sub Categories")} className="mb-3">
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={activeMeta.sub_category_ids}
                            placeholder={lang("Select Sub Categories")}
                            optionFilterProp="label"
                            onChange={(values) => updateActiveMeta("sub_category_ids", values)}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                          >
                            {filteredSubCategoryOptions.map((item) => (
                              <Select.Option key={item.value} value={item.value} label={item.label}>
                                {item.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item label={lang("Occasions")} className="mb-3">
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={activeMeta.occasion_ids}
                            placeholder={lang("Select Occasions")}
                            optionFilterProp="label"
                            onChange={(values) => updateActiveMeta("occasion_ids", values)}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                          >
                            {occasionOptions.map((item) => (
                              <Select.Option key={item.value} value={item.value} label={item.label}>
                                {item.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item label={lang("Keywords")} className="mb-0">
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={activeMeta.keywords_ids}
                            placeholder={lang("Select Keywords")}
                            optionFilterProp="label"
                            onChange={(values) => updateActiveMeta("keywords_ids", values)}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                          >
                            {keywordOptions.map((item) => (
                              <Select.Option key={item.value} value={item.value} label={item.label}>
                                {item.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </>
                  ) : (
                    <Empty description={lang("Select a preview card to edit its mapping.")} />
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default ScrapeProductModal;
